import React from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import {
  FaExternalLinkAlt,
  FaGithub,
  FaHome,
  FaChessBoard,
  FaYinYang,
} from "react-icons/fa";
import {
  FaWater,
  FaMugHot,
  FaLightbulb,
  FaRocket,
  FaEarthAmericas,
  FaGear,
} from "react-icons/fa6";
import { IoGlobeOutline } from "react-icons/io5";

const ProjectsSection = styled.section`
  padding: 100px 20px;
  background: rgba(255, 255, 255, 0.95);
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 3rem;
  color: #333;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(45deg, #667eea, #764ba2);
    border-radius: 2px;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProjectCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ProjectImage = styled.div`
  height: 150px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
`;

const ProjectDescription = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-weight: 500;
  transition: all 0.3s ease;

  &.live {
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
  }

  &.github {
    background: #333;
    color: white;
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const projects = [
  {
    id: 1,
    title: "HOUNTED-HOUSE",
    description: "简易版鬼屋",
    image: <FaHome />,
    tags: ["Three.js", "Vite", "lil-gui"],
    liveUrl: "/hounted-house/",
    githubUrl: "https://github.com/username/ecommerce-platform",
  },
  {
    id: 2,
    title: "MIXING-HTML-AND-WEBGL",
    description: "html和webgl的融合",
    image: <FaYinYang />,
    tags: ["Three.js", "Vite", "HTML"],
    liveUrl: "/mixing-html-and-webgl/",
    githubUrl: "https://github.com/username/ecommerce-platform",
  },
  {
    id: 3,
    title: "RAGING-SEA",
    description:
      "波涛汹涌的大海，大的海浪中夹杂着无数个小的海浪(噪声函数实现小海浪的随机性)。",
    image: <FaWater />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/raging-sea/",
    githubUrl: "https://github.com/username/task-manager",
  },
  {
    id: 4,
    title: "ANIMATED-GALAXY",
    description: "粒子构建的宇宙行星天体",
    image: <IoGlobeOutline />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/animated-galaxy/",
    githubUrl: "https://github.com/username/weather-dashboard",
  },
  {
    id: 5,
    title: "COFFEE-SMOKE",
    description: "模拟咖啡杯中冒出的动画烟雾，利用了噪声图模拟出烟、雾的效果",
    image: <FaMugHot />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/coffee-smoke-shader/",
    githubUrl: "https://github.com/username/portfolio-website",
  },
  {
    id: 6,
    title: "HOLOGRAM",
    description: "全息投影实现了Fresnel效果(外表比里面更亮)",
    image: <FaLightbulb />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/hologram-shader/",
    githubUrl: "https://github.com/username/social-analytics",
  },
  {
    id: 7,
    title: "FIREWORKS",
    description: "五彩斑斓的烟花包括(爆炸-变大-下降-缩小-闪烁)5个阶段",
    image: <FaRocket />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/fireworks/",
    githubUrl: "https://github.com/username/recipe-platform",
  },
  {
    id: 8,
    title: "EARTH",
    description: "美丽又逼真的地球，白天和黑夜交织，外表还有淡淡的大气层",
    image: <FaEarthAmericas />,
    tags: ["Three.js", "Shader"],
    liveUrl: "/earth/",
    githubUrl: "https://github.com/username/recipe-platform",
  },
  {
    id: 9,
    title: "CURSOR-ANIMATION",
    description:
      "在由粒子组成的图片上创建一个很酷的光标动画(2D Canvas绘制鼠标的轨迹)",
    image: <FaChessBoard />,
    tags: ["Three.js", "Shader", "2D Canvas"],
    liveUrl: "/cursor-animation/",
    githubUrl: "https://github.com/username/recipe-platform",
  },
  {
    id: 10,
    title: "SLICED-MODEL",
    description: "一个可以切片的模型，通过着色器实现",
    image: <FaGear />,
    tags: ["Three.js", "Shader", "customer-shader-material"],
    liveUrl: "/sliced-model/",
    githubUrl: "https://github.com/username/sliced-model-shader",
  },
  {
    id: 11,
    title: "PARTICLES-MORPHING-SHADER",
    description: "粒子变形着色器效果，展示流体形态和形状变换",
    image: <FaEarthAmericas />,
    tags: ["Three.js", "Shader", "Particles"],
    liveUrl: "/particles-morphing-shader/",
    githubUrl: "https://github.com/username/particles-morphing-shader",
  },
];

const Projects = () => {
  return (
    <ProjectsSection>
      <SectionTitle>我的Webgl \ Three.js \ Web3D Demo</SectionTitle>
      <ProjectsGrid>
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -5 }}
          >
            <ProjectImage>{project.image}</ProjectImage>
            <ProjectContent>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectTags>
                {project.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </ProjectTags>
              <ProjectLinks>
                <ProjectLink
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="live"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaExternalLinkAlt /> 在线演示
                </ProjectLink>
                <ProjectLink
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="github"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaGithub /> GitHub
                </ProjectLink>
              </ProjectLinks>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
    </ProjectsSection>
  );
};

export default Projects;
